import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { voiceActorService } from '../../../services/voiceActorService';

const AvailabilityCalendar = ({ voiceActorId, sessions }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAvailability = async () => {
      if (!voiceActorId) return;

      try {
        setLoading(true);
        const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1)?.toISOString()?.split('T')?.[0];
        const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0)?.toISOString()?.split('T')?.[0];

        const availabilityData = await voiceActorService?.getAvailability(
          voiceActorId,
          startDate,
          endDate
        );
        setAvailability(availabilityData);
      } catch (err) {
        console.error('Error fetching availability:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailability();
  }, [voiceActorId, currentMonth]);

  const getDaysInMonth = () => {
    const year = currentMonth?.getFullYear();
    const month = currentMonth?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();

    return { daysInMonth, startingDayOfWeek };
  };

  const getDateStatus = (day) => {
    const dateStr = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)?.toISOString()?.split('T')?.[0];
    
    const hasSession = sessions?.some(s => s?.scheduledDate === dateStr);
    const availabilitySlot = availability?.find(a => a?.date === dateStr);

    if (hasSession) return 'session';
    if (availabilitySlot) {
      if (availabilitySlot?.status === 'available') return 'available';
      if (availabilitySlot?.status === 'busy') return 'busy';
      if (availabilitySlot?.status === 'unavailable') return 'unavailable';
    }
    return 'default';
  };

  const getDateColor = (status) => {
    switch (status) {
      case 'session': return 'bg-primary text-white';
      case 'available': return 'bg-success/20 text-success';
      case 'busy': return 'bg-warning/20 text-warning';
      case 'unavailable': return 'bg-error/20 text-error';
      default: return 'bg-transparent text-foreground';
    }
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth();
  const monthName = currentMonth?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-card-foreground flex items-center gap-2">
          <Icon name="Calendar" size={24} className="text-primary" />
          Availability
        </h2>
      </div>
      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={previousMonth}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
          <Icon name="ChevronLeft" size={20} className="text-foreground" />
        </button>
        <h3 className="text-lg font-semibold text-foreground">{monthName}</h3>
        <button
          onClick={nextMonth}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
          <Icon name="ChevronRight" size={20} className="text-foreground" />
        </button>
      </div>
      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 mb-4">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S']?.map((day, index) => (
          <div key={index} className="text-center text-xs font-medium text-muted-foreground py-2">
            {day}
          </div>
        ))}
        {Array.from({ length: startingDayOfWeek })?.map((_, index) => (
          <div key={`empty-${index}`} className="aspect-square" />
        ))}
        {Array.from({ length: daysInMonth })?.map((_, index) => {
          const day = index + 1;
          const status = getDateStatus(day);
          const isToday = 
            day === new Date()?.getDate() &&
            currentMonth?.getMonth() === new Date()?.getMonth() &&
            currentMonth?.getFullYear() === new Date()?.getFullYear();

          return (
            <div
              key={day}
              className={`aspect-square flex items-center justify-center text-sm rounded-lg transition-colors ${
                getDateColor(status)
              } ${
                isToday ? 'ring-2 ring-primary ring-offset-2' : ''
              }`}
            >
              {day}
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div className="space-y-2 pt-4 border-t border-border">
        <div className="flex items-center gap-2 text-xs">
          <div className="w-4 h-4 rounded bg-primary"></div>
          <span className="text-muted-foreground">Recording Session</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <div className="w-4 h-4 rounded bg-success/20"></div>
          <span className="text-muted-foreground">Available</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <div className="w-4 h-4 rounded bg-warning/20"></div>
          <span className="text-muted-foreground">Busy</span>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;